/** QuickPlan — Formats de panneaux / planches disponibles chez le fournisseur (réglages du
 *  projet), trait de scie et rognage. Deux présentations : tableau (notice, feuille blanche)
 *  ou fiches compactes (panneau Propriétés). Sert au plan de coupe (shared/cutting.js). */
import { useProjectStore } from '../store/useProjectStore.js';
import { SHEET_PRESETS, DEFAULT_SHEET } from '../../shared/cutting.js';
import { newId } from '../../shared/schema.js';
import NumField from './NumField.jsx';
import TextField from './TextField.jsx';

export default function SheetStock({ cut, compact = false }) {
  const project = useProjectStore((s) => s.project);
  const updateProject = useProjectStore((s) => s.updateProject);
  const readOnly = project.role === 'viewer';
  const s = project.settings;
  const sheets = s.sheets ?? [];
  const groups = cut?.groups ?? [];
  const set = (fn) => { if (!readOnly) updateProject((p) => { p.settings.sheets = p.settings.sheets ?? []; fn(p.settings); }); };
  const patch = (id, v) => set((st) => { Object.assign(st.sheets.find((x) => x.id === id), v); });
  const remove = (id) => set((st) => { st.sheets = st.sheets.filter((y) => y.id !== id); });
  const materials = [...new Set(groups.map((g) => g.material))];
  const missing = groups.filter((g) => g.defaultFormat);
  // Épaisseur proposée : la plus fréquente parmi les pièces à découper.
  const thick = groups.slice().sort((a, b) => b.count - a.count)[0]?.thickness ?? 18;
  const addPreset = (i) => {
    const f = SHEET_PRESETS[i];
    if (f) set((st) => { st.sheets.push({ id: newId('s'), material: materials.length === 1 ? materials[0] : '', thickness: thick, length: f.length, width: f.width, grain: false, ...(f.trim != null ? { trim: f.trim } : {}) }); });
  };
  const addBlank = () => set((st) => { st.sheets.push({ id: newId('s'), material: materials.length === 1 ? materials[0] : '', thickness: thick, length: 2500, width: 500, grain: false, trim: 0 }); });

  const f = {
    material: (x) => <TextField value={x.material} max={80} list="qp-materials" placeholder="toutes" disabled={readOnly} onCommit={(v) => patch(x.id, { material: v.trim() })} />,
    thickness: (x) => <NumField value={x.thickness} disabled={readOnly} onCommit={(v) => v > 0 && patch(x.id, { thickness: v })} />,
    length: (x) => <NumField value={x.length} disabled={readOnly} onCommit={(v) => v > 0 && patch(x.id, { length: v })} />,
    width: (x) => <NumField value={x.width} disabled={readOnly} onCommit={(v) => v > 0 && patch(x.id, { width: v })} />,
    price: (x) => <NumField value={x.price} allowNull disabled={readOnly} onCommit={(v) => patch(x.id, { price: v == null ? undefined : Math.max(0, v) })} />,
    trim: (x) => <NumField value={x.trim} allowNull placeholder={String(s.sheetTrim ?? 10)} disabled={readOnly} onCommit={(v) => patch(x.id, { trim: v == null ? undefined : Math.max(0, Math.min(100, v)) })} />,
    grain: (x) => <input type="checkbox" checked={!!x.grain} disabled={readOnly} onChange={(e) => patch(x.id, { grain: e.target.checked })} />,
  };

  const actions = !readOnly && (
    <div className="row wrap stock-actions">
      <select value="" onChange={(e) => addPreset(Number(e.target.value))}>
        <option value="">＋ Format courant…</option>
        {SHEET_PRESETS.map((p, i) => <option key={p.label} value={i}>{p.label}</option>)}
      </select>
      <button onClick={addBlank}>＋ Format libre</button>
      {missing.length > 0 && (
        <button title="Une ligne 2800 × 2070 par matière / épaisseur sans format" onClick={() => set((st) => { for (const g of missing) st.sheets.push({ id: newId('s'), material: g.material, thickness: g.thickness, length: DEFAULT_SHEET.length, width: DEFAULT_SHEET.width, grain: false }); })}>
          ＋ Une ligne par matière ({missing.length})
        </button>
      )}
    </div>
  );
  const datalist = <datalist id="qp-materials">{materials.map((m) => <option key={m} value={m} />)}</datalist>;
  const general = (
    <>
      <label>Trait de scie (mm)<NumField value={s.sheetKerf ?? 4} disabled={readOnly} onCommit={(v) => v >= 0 && v <= 20 && set((st) => { st.sheetKerf = v; })} /></label>
      <label>Rognage par défaut (mm / bord)<NumField value={s.sheetTrim ?? 10} disabled={readOnly} onCommit={(v) => v >= 0 && v <= 100 && set((st) => { st.sheetTrim = v; })} /></label>
    </>
  );

  if (compact) {
    return (
      <div className="stock-compact">
        {sheets.map((x) => (
          <div key={x.id} className="stock-card">
            <div className="form-grid">
              <label className="span2">Matière (vide = toutes){f.material(x)}</label>
              <label>Longueur (mm){f.length(x)}</label>
              <label>Largeur (mm){f.width(x)}</label>
              <label>Épaisseur (mm){f.thickness(x)}</label>
              <label>Prix € / unité{f.price(x)}</label>
              <label>Rognage (mm / bord){f.trim(x)}</label>
              <label className="check" title="Décor ou fil du bois : les pièces ne sont pas tournées">{f.grain(x)} Fil (sans rotation)</label>
            </div>
            {!readOnly && <button className="icon danger stock-del" title="Supprimer ce format" onClick={() => remove(x.id)}>✕</button>}
          </div>
        ))}
        {!sheets.length && <div className="muted">Aucun format : un panneau {DEFAULT_SHEET.length} × {DEFAULT_SHEET.width} est supposé.</div>}
        {actions}
        <div className="form-grid">{general}</div>
        {datalist}
      </div>
    );
  }

  return (
    <div className="stock noprint">
      <div className="stock-head">
        <b>Panneaux et planches disponibles chez le fournisseur</b>
        <span className="grow" />
        {general}
      </div>
      <table className="stock-table">
        <thead><tr><th>Matière (vide = toutes)</th><th>Ép.</th><th>Longueur</th><th>Largeur</th><th>Prix € / unité</th><th title="Vide = rognage par défaut ; 0 pour une planche aux chants finis">Rognage</th><th title="Décor ou fil du bois : les pièces ne sont pas tournées">Fil</th><th /></tr></thead>
        <tbody>
          {sheets.map((x) => (
            <tr key={x.id}>
              <td>{f.material(x)}</td><td>{f.thickness(x)}</td><td>{f.length(x)}</td><td>{f.width(x)}</td><td>{f.price(x)}</td><td>{f.trim(x)}</td><td>{f.grain(x)}</td>
              <td>{!readOnly && <button className="icon danger" onClick={() => remove(x.id)}>✕</button>}</td>
            </tr>
          ))}
          {!sheets.length && <tr><td colSpan={8} className="dim">Aucun format saisi : un panneau {DEFAULT_SHEET.length} × {DEFAULT_SHEET.width} est supposé pour chaque épaisseur.</td></tr>}
        </tbody>
      </table>
      {datalist}
      {actions}
    </div>
  );
}
